import React from 'react';
import PropTypes from 'prop-types';
import OmForm from '../../ui/OmForm';
import OmButton from '../../ui/OmButton';
import DynamicFieldRenderer from '../DynamicFieldRenderer';
import progressiveDisclosureService, { getClaimTypeDisplayName } from '../../../services/progressiveDisclosureService';

/**
 * Step 3: Claim Details Component
 * 
 * Step 4.2: Implement Progressive Disclosure Based on User Stories
 * 
 * Handles type-specific claim details using progressive disclosure:
 * - Shows universal fields for all claim types
 * - Shows type-specific fields only when relevant
 * - Flags fields that require manual review vs automated validation
 */
const Step3ClaimDetails = ({
  formData,
  errors,
  visibleFields,
  fieldMetadata,
  claimCauses,
  isLoading,
  onInputChange,
  onNext,
  onPrevious,
  schema
}) => {
  // Get progressive disclosure configuration for the selected claim type
  const progressiveConfig = progressiveDisclosureService.getFieldsForClaimType(formData.type);
  
  // Handle form submission for this step
  const handleStepSubmit = (e) => {
    e.preventDefault();
    if (onNext) {
      onNext();
    }
  };

  // Render loading state if schema is not available
  if (!schema || !formData.type) {
    return (
      <div className="text-center py-12">
        <div className="mb-4">
          <svg className="w-16 h-16 mx-auto text-steel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <p className="text-steel text-lg">
          {!schema ? 'Schema not loaded yet' : 'Please select a claim type in step 1 to continue'}
        </p>
        <p className="text-steel text-sm mt-2">
          {!schema 
            ? 'Please wait while we load the form schema...' 
            : 'Select a claim type to see relevant fields'}
        </p>
      </div>
    );
  }

  // Get only type-specific fields (universal fields are handled in Steps 1 & 2)
  const typeSpecificFields = progressiveConfig.typeSpecificFields;
  
  // Filter fields based on what's available in schema and visibility rules
  let filteredFields = typeSpecificFields.filter(fieldName => 
    visibleFields.includes(fieldName) && fieldMetadata[fieldName]
  );

  // Conditional logic: Only show illness stage for cancer-related diagnoses
  if (formData.type === '002') { // Critical illness claims
    const isCancerRelated = formData.diagnosis && 
      formData.diagnosis.toLowerCase().includes('cancer');
    
    if (!isCancerRelated) {
      // Remove illness stage field for non-cancer conditions BEFORE rendering
      filteredFields = filteredFields.filter(fieldName => fieldName !== 'illnessStage');
    }
  }

  // DEBUG: Log field filtering for parentRelationshipVerified
  if (formData.type === '013') {
    console.log('🐛 DEBUG Step3 field filtering for type 013:', {
      typeSpecificFields,
      visibleFields,
      filteredFields,
      hasParentRelationshipVerified: filteredFields.includes('parentRelationshipVerified'),
      parentRelationshipMetadata: fieldMetadata['parentRelationshipVerified'],
      allFieldMetadata: Object.keys(fieldMetadata)
    });
  }

  // Compute display name for conditional section header rendering
  const typeDisplayName = formData.type ? getClaimTypeDisplayName(formData.type) : 'Claim';

  // Group fields by sections for better organization
  const renderFieldSection = (sectionName, fields) => {
    if (!fields || fields.length === 0) return null;

    const isMainTypeSpecificSection = sectionName === `${typeDisplayName} Details`;

    return (
      <div key={sectionName} className="space-y-4">
        {!isMainTypeSpecificSection && (
          <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
            {sectionName}
          </h3>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {fields.map(fieldName => {
            // Helper function to determine if field should use claim causes
            const isCauseField = (field) => {
              return field === 'causeOfDeath' || 
                     field === 'causeOfDisability' || 
                     field === 'disabilityCause' || 
                     field === 'illnessCause' ||
                     field === 'diagnosis' && (formData.type === '002' || formData.type === '004' || formData.type === '006'); // Include TTD (006)
            };
            
            // Prepare field metadata with claim causes for cause fields
            const enhancedFieldMetadata = {
              ...fieldMetadata[fieldName]
            };
            
            // Add claim causes as options for cause fields
            if (isCauseField(fieldName) && claimCauses && claimCauses.length > 0) {
              enhancedFieldMetadata.type = 'select';
              enhancedFieldMetadata.options = claimCauses.map((cause, index) => ({
                value: cause.description || `unknown-${index}`,
                label: cause.description || 'Unknown',
                key: cause.id || `cause-${index}`
              }));
              // Use a consistent label for cause fields across claim types
              if (fieldName === 'diagnosis' || fieldName === 'causeOfDisability' || fieldName === 'illnessCause') {
                enhancedFieldMetadata.label = 'Claim Cause';
              }
            }
            
            // Add illness stage dropdown options
            if (fieldName === 'illnessStage') {
              enhancedFieldMetadata.type = 'select';
              enhancedFieldMetadata.options = [
                { value: 'I', label: 'Stage I' },
                { value: 'II', label: 'Stage II' },
                { value: 'III', label: 'Stage III' },
                { value: 'IV', label: 'Stage IV' },
                { value: 'OTHER', label: 'Other Stage' }
              ];
            }

            // Hide monthlyPaymentAmount for TTD - computed server-side
            if (formData.type === '006' && fieldName === 'monthlyPaymentAmount') {
              return null;
            }

            // Hide Payment Start/End Dates for TTD - auto-calculated on approval
            if (formData.type === '006' && (fieldName === 'paymentStartDate' || fieldName === 'paymentEndDate')) {
              return null;
            }

            // Relabel estimatedPaymentDuration for clarity
            if (fieldName === 'estimatedPaymentDuration') {
              enhancedFieldMetadata.label = 'Payment Period (months)';
            }
            
            // Hide employmentStatusConfirmed (it will be auto-ticked based on documentation)
            if (fieldName === 'employmentStatusConfirmed') {
              return null;
            }

            return (
              <div key={fieldName} className="space-y-2">
                <DynamicFieldRenderer
                  fieldName={fieldName}
                  fieldMetadata={enhancedFieldMetadata}
                  value={formData[fieldName]}
                  onChange={onInputChange}
                  error={errors[fieldName]}
                  disabled={isLoading}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <OmForm
        titleText={`${typeDisplayName} Claim Details`}
        descriptionText={`Please provide all required information specific to your ${typeDisplayName.toLowerCase()} claim.`}
        onSubmit={handleStepSubmit}
        formId="claim-details-form"
        submitButtonLabel="Continue to Documents"
        hideSubmitButton={false}
      >
        <div className="space-y-8">
          {/* Render only type-specific field sections */}
          {Object.entries(progressiveConfig.fieldSections).map(([sectionName, fields]) => {
            // Skip universal information section since it's handled in Steps 1 & 2
            if (sectionName === 'Universal Information') {
              return null;
            }
            
            // Only show fields that are actually visible and available
            const availableFields = fields.filter(fieldName => 
              filteredFields.includes(fieldName)
            );
            
            return renderFieldSection(sectionName, availableFields);
          })}
          
          {/* Show any remaining type-specific fields not in sections */}
          {(() => {
            const fieldsInSections = Object.values(progressiveConfig.fieldSections).flat();
            const remainingFields = filteredFields.filter(fieldName => 
              !fieldsInSections.includes(fieldName)
            );
            
            if (remainingFields.length > 0) {
              return renderFieldSection('Additional Information', remainingFields);
            }
            return null;
          })()}
          
          {filteredFields.length === 0 && (
            <div className="text-center py-8">
              <p className="text-steel">No additional fields required for this claim type.</p>
            </div>
          )}
        </div>

        {/* Previous Button in actions slot */}
        <div slot="actions">
          <div 
            style={{
              '--omk-outlined-button-label-text-color': 'var(--color-steel)',
              '--omk-outlined-button-icon-color': 'var(--color-steel)',
              '--omk-outlined-button-outline-color': 'var(--color-silver-mist)',
              '--omk-outlined-button-hover-label-text-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-icon-color': 'var(--color-deep-jungle)',
              '--omk-outlined-button-hover-state-layer-color': 'var(--color-silver-mist)'
            }}
          >
            <OmButton
              type="button"
              onClick={onPrevious}
              disabled={isLoading}
              buttonType="outlined-button"
              buttonSize="medium"
              leadingIcon="arrow_back"
            >
              Previous
            </OmButton>
          </div>
        </div>
      </OmForm>
    </div>
  );
};

Step3ClaimDetails.propTypes = {
  formData: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  visibleFields: PropTypes.array.isRequired,
  fieldMetadata: PropTypes.object.isRequired,
  claimCauses: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func,
  schema: PropTypes.object
};

Step3ClaimDetails.defaultProps = {
  onPrevious: null,
  schema: null,
  claimCauses: []
};

export default Step3ClaimDetails;